/**
 * SAMANVAI - ProposalGenerator.jsx
 * Drop onto the Value Scorecard page:
 *   <ProposalGenerator assessmentId={assessment.id} companyName={company.name} />
 *
 * Needs: axios, react-markdown, remark-gfm, Tailwind (all standard in Emergent React apps).
 *   yarn add react-markdown remark-gfm
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Copy, Download, Printer, Plus, X } from 'lucide-react';
import './ProposalGenerator.css';
import { ProposalSourceContext } from './ProposalSourceContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/proposals`;

const FIELDS = [
  { key: "business_objectives", label: "Business objectives", ph: "What outcomes does the client want in the next 12-24 months?", area: true },
  { key: "pain_points", label: "Pain points", ph: "Problems the client raised in their own words", area: true },
  { key: "success_criteria", label: "Success criteria", ph: "How will the client judge success?", area: true },
  { key: "budget_range", label: "Budget range", ph: "e.g. INR 40-60 lakh / USD 150-250k" },
  { key: "target_timeline", label: "Target timeline", ph: "e.g. Quick wins in 90 days, scale by FY27" },
  { key: "decision_makers", label: "Decision makers", ph: "Sponsor and buying committee roles" },
  { key: "current_systems", label: "Current systems", ph: "ERP, data platforms, OT systems, AI tools" },
  { key: "prior_initiatives", label: "Prior initiatives", ph: "What was tried before and what happened", area: true },
  { key: "constraints", label: "Constraints", ph: "Regulatory, data residency, capacity, etc.", area: true },
  { key: "out_of_scope", label: "Out of scope", ph: "Anything the client has excluded" },
  { key: "preferred_engagement_model", label: "Preferred engagement model", ph: "e.g. fixed fee, phased, value-share" },
  { key: "tone_preference", label: "Tone", ph: "e.g. formal, concise, board-ready" },
  { key: "rfp_reference", label: "RFP reference no.", ph: "Optional" },
  { key: "rfp_requirements", label: "RFP requirements", ph: "Paste one requirement per line (optional)", area: true, rows: 5 },
  { key: "other_notes", label: "Other notes", ph: "Anything else the client wants considered", area: true },
];

const MODES = [
  { v: "executive", label: "Executive proposal", hint: "Shorter, lower-cost draft" },
  { v: "full", label: "Full proposal", hint: "All 9 sections" },
  { v: "both", label: "Both + recommendation", hint: "Longer output, higher usage" },
];

const readinessStyle = {
  "Ready to send": "bg-emerald-100 text-emerald-800",
  "Needs minor edits": "bg-amber-100 text-amber-800",
  "Needs major input": "bg-rose-100 text-rose-800",
};

export default function ProposalGenerator({ assessmentId, companyName, userId }) {
  const [mode, setMode] = useState("executive");
  const [showExtra, setShowExtra] = useState(false);
  const [info, setInfo] = useState({});
  const [custom, setCustom] = useState([{ k: "", v: "" }]);
  const [scope, setScope] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [proposal, setProposal] = useState(null);
  const [history, setHistory] = useState([]);
  const [notice, setNotice] = useState('');
  const [context, setContext] = useState(null);
  const [reviewAll, setReviewAll] = useState(false);
  const [contextRetry, setContextRetry] = useState(0);
  const [proposalText, readinessNotes] = (proposal?.markdown || '').split(/^##\s+Proposal Readiness Notes\s*$/im);
  const clientMarkdown = proposal
    ? `> DRAFT — All planning estimates and assumptions require verification before use. These are not approved prices, client commitments or guaranteed outcomes. Confirm source figures and all [TO CONFIRM] items.\n\n${proposalText}`
    : proposalText;
  const active = useRef(null);
  const reportBody = useRef(null);
  const headers = () => {
    const token = localStorage.getItem(`vsc_token_${userId}`);
    return token ? { 'X-Access-Token': token } : {};
  };
  const errorText = e => typeof e?.response?.data?.detail === 'string'
    ? e.response.data.detail : e?.response?.status === 422 ? 'Please shorten or check the optional client information.'
      : e.message || 'Proposal generation did not complete.';
  const waitForResult = async (response, signal) => {
    const deadline = Date.now() + 520000;
    while (response.status === 202 && Date.now() < deadline) {
      await new Promise((resolve, reject) => {
        const cancel = () => { clearTimeout(timer); reject(new axios.CanceledError()); };
        const timer = setTimeout(() => { signal.removeEventListener('abort', cancel); resolve(); }, 3000);
        signal.addEventListener('abort', cancel, { once: true });
      });
      response = await axios.get(`${API}/jobs/${assessmentId}`, { headers: headers(), signal, timeout: 20000 });
    }
    if (response.status === 202) throw new Error('Your request is still processing. Reopen this page to check it; no automatic regeneration will run.');
    return response;
  };

  // Load previously generated proposals (free - no LLM call)
  useEffect(() => {
    if (!assessmentId || !userId) { setLoading(false); return; }
    const controller = new AbortController();
    active.current = controller;
    setLoading(true); setError(''); setHistory([]); setProposal(null);
    setContext(null); setReviewAll(false);
    setMode('executive'); setInfo({}); setScope(''); setCustom([{ k: '', v: '' }]); setNotice('');
    const config = { headers: headers(), signal: controller.signal, timeout: 20000 };
    (async () => {
      try {
        const [saved, loaded] = await Promise.all([
          axios.get(`${API}/${assessmentId}`, config), axios.get(`${API}/context/${assessmentId}`, config),
        ]);
        setContext(loaded.data);
        const merged = { ...loaded.data.prefilled, ...loaded.data.additional_info };
        const { scope_focus, custom_fields, ...fields } = merged;
        setInfo(fields); setScope((scope_focus || []).join(', '));
        setCustom(Object.keys(custom_fields || {}).length ? Object.entries(custom_fields).map(([k, v]) => ({ k, v })) : [{ k: '', v: '' }]);
        setShowExtra(true);
        setHistory(saved.data || []);
        if (saved.data?.length) setProposal(saved.data[0]);
        const pending = await axios.get(`${API}/jobs/${assessmentId}`, config);
        if (pending.status === 202) {
          const result = await waitForResult(pending, controller.signal);
          setProposal(result.data);
          setHistory(h => [result.data, ...h.filter(item => item.id !== result.data.id)].slice(0, 5));
        }
      } catch (e) { if (!controller.signal.aborted) setError(errorText(e)); }
      finally { if (!controller.signal.aborted) setLoading(false); if (active.current === controller) active.current = null; }
    })();
    return () => { controller.abort(); active.current?.abort(); active.current = null; };
    // Reopen only when the authenticated scorecard changes; never auto-generate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId, userId, contextRetry]);

  const buildAdditionalInfo = () => {
    const out = {};
    Object.entries(info).forEach(([k, v]) => {
      if (typeof v === 'string' && v.trim() && v.trim() !== String(context?.prefilled?.[k] || '').trim()) out[k] = v.trim();
    });
    const scopeList = scope.split(",").map((s) => s.trim()).filter(Boolean);
    if (scopeList.length && JSON.stringify(scopeList) !== JSON.stringify(context?.prefilled?.scope_focus || [])) out.scope_focus = scopeList;
    const cf = {};
    custom.forEach(({ k, v }) => { if (k.trim() && v.trim()) cf[k.trim()] = v.trim(); });
    if (Object.keys(cf).length) out.custom_fields = cf;
    return Object.keys(out).length ? out : null;
  };

  const generate = async (force = false) => {
    if (active.current || loading || !context) return;
    if (force && !window.confirm('Regenerate makes one new AI request and uses credits. Continue?')) return;
    const controller = new AbortController();
    active.current = controller;
    setLoading(true); setError("");
    try {
      let r = await axios.post(`${API}/generate`, {
        assessment_id: assessmentId,
        mode,
        additional_info: buildAdditionalInfo(),
        force_regenerate: force,
      }, { timeout: 20000, headers: headers(), signal: controller.signal });
      r = await waitForResult(r, controller.signal);
      setProposal(r.data);
      setHistory((h) => [r.data, ...h.filter((x) => x.id !== r.data.id)].slice(0, 5));
    } catch (e) {
      if (!controller.signal.aborted) setError(errorText(e));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
      if (active.current === controller) active.current = null;
    }
  };

  const remainingGaps = (context?.missing || []).filter(item => item.key === 'scope_focus' ? !scope.trim() : !String(info[item.key] || '').trim());
  const visibleFields = FIELDS.filter(field => reviewAll || !context?.prefilled?.[field.key]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(clientMarkdown.trim()); setNotice('Proposal copied.'); }
    catch { setError('Clipboard access is unavailable. Please use Download .md instead.'); }
  };
  const download = () => {
    const blob = new Blob([clientMarkdown.trim()], { type: "text/markdown" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `SAMANVAI_Proposal_${(companyName || "Client").replace(/\s+/g, "_")}_${proposal.mode}.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  const printPdf = () => {
    const w = window.open("", "_blank");
    if (!w) { setError('Please allow the print window, or download the Markdown file.'); return; }
    w.opener = null;
    const html = reportBody.current.innerHTML;
    w.document.write(`<html><head><title>SAMANVAI Proposal</title>
      <style>body{font-family:Georgia,serif;max-width:820px;margin:32px auto;color:#141B22;line-height:1.55;overflow-wrap:anywhere}
      h1,h2{color:#12968A}table{border-collapse:collapse;width:100%;margin:12px 0}
      th,td{border:1px solid #cfd8dc;padding:6px 8px;text-align:left;font-size:13px}th{background:#eef7f5}@media print{.overflow-x-auto{overflow:visible!important}}</style>
      </head><body>${html}</body></html>`);
    w.document.close(); w.focus(); w.print();
  };

  return (
    <section id="client-proposal" className="mt-8 scroll-mt-20 border-t border-gray-200 bg-white px-4 py-6 sm:px-8" data-testid="proposal-generator">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 data-testid="proposal-generator-heading" className="text-base md:text-lg font-semibold text-gray-900">Create Client Proposal</h2>
        </div>
      </div>

      <ProposalSourceContext context={context} missing={remainingGaps} reviewAll={reviewAll}
        onReview={() => { setReviewAll(value => !value); setShowExtra(true); }} />

      {/* Mode */}
      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        {MODES.map((m) => (
          <button key={m.v} onClick={() => setMode(m.v)} disabled={loading} aria-pressed={mode === m.v} data-testid={`mode-${m.v}`}
            className={`rounded-lg border p-3 text-left transition-colors ${mode === m.v ? "border-teal-600 bg-teal-50" : "border-slate-200 hover:border-slate-300"}`}>
            <div className="font-medium text-slate-900">{m.label}</div>
            <div className="text-xs text-slate-500">{m.hint}</div>
          </button>
        ))}
      </div>

      {/* Optional additional info */}
      <button onClick={() => setShowExtra(!showExtra)} data-testid="toggle-additional-info" aria-expanded={showExtra}
        className="mt-5 text-sm font-medium text-teal-700 hover:underline">
        {showExtra ? "− Hide" : "+ Add"} missing or additional client information
      </button>

      {showExtra && (
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {(reviewAll || !context?.prefilled?.scope_focus) && <label className="sm:col-span-2 text-sm">
            <span className="font-medium text-slate-700">Scope focus</span>
            <input data-testid="proposal-scope-focus" disabled={loading} maxLength={8000} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              placeholder="Comma-separated: e.g. Plant 2, Procurement, AI Governance lens"
              value={scope} onChange={(e) => setScope(e.target.value)} />
          </label>}
          {visibleFields.map((f) => (
            <label key={f.key} className={`text-sm ${f.area ? "sm:col-span-2" : ""}`}>
              <span className="font-medium text-slate-700">{f.label}</span>
              {context?.prefilled?.[f.key] && <span data-testid={`proposal-field-source-${f.key}`} className="ml-2 text-xs text-teal-700">From Value Scorecard</span>}
              {f.area ? (
                <textarea data-testid={`proposal-field-${f.key}`} disabled={loading} maxLength={12000} rows={f.rows || 2} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  placeholder={f.ph} value={info[f.key] || ""}
                  onChange={(e) => setInfo({ ...info, [f.key]: e.target.value })} />
              ) : (
                <input data-testid={`proposal-field-${f.key}`} disabled={loading} maxLength={8000} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  placeholder={f.ph} value={info[f.key] || ""}
                  onChange={(e) => setInfo({ ...info, [f.key]: e.target.value })} />
              )}
            </label>
          ))}
          <div className="sm:col-span-2">
            <span className="text-sm font-medium text-slate-700">Custom fields</span>
            {custom.map((row, i) => (
              <div key={i} className="mt-2 flex gap-2">
                <input data-testid={`proposal-custom-key-${i}`} aria-label={`Custom field ${i + 1} name`} disabled={loading} maxLength={100} className="min-w-0 w-1/3 rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Field"
                  value={row.k} onChange={(e) => setCustom(custom.map((r, j) => j === i ? { ...r, k: e.target.value } : r))} />
                <input data-testid={`proposal-custom-value-${i}`} aria-label={`Custom field ${i + 1} value`} disabled={loading} maxLength={2000} className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Value"
                  value={row.v} onChange={(e) => setCustom(custom.map((r, j) => j === i ? { ...r, v: e.target.value } : r))} />
                {custom.length > 1 && (
                  <button data-testid={`proposal-custom-remove-${i}`} disabled={loading} aria-label={`Remove custom field ${i + 1}`} title="Remove field" className="px-2 text-slate-400 hover:text-rose-600" onClick={() => setCustom(custom.filter((_, j) => j !== i))}><X size={16} /></button>
                )}
              </div>
            ))}
            <button data-testid="proposal-custom-add" disabled={loading || custom.length >= 20} className="mt-2 text-xs text-teal-700 hover:underline inline-flex items-center gap-1" onClick={() => setCustom([...custom, { k: "", v: "" }])}><Plus size={14} />Add field</button>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button onClick={() => generate(false)} disabled={loading || !assessmentId || !context} data-testid="generate-proposal-btn"
          className="rounded-lg bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-50">
          {loading ? "Please wait…" : "Generate Proposal"}
        </button>
        {proposal && !loading && (
          <button onClick={() => generate(true)} className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm hover:bg-slate-50"
            data-testid="regenerate-proposal-btn" title="Forces a new LLM call (uses credits)">
            Regenerate
          </button>
        )}
        {error && <span role="alert" data-testid="proposal-generator-error" className="text-sm text-rose-600">{error}</span>}
        {!context && !loading && <button data-testid="proposal-reload-context" onClick={() => setContextRetry(value => value + 1)} className="text-sm text-teal-700 underline">Reload scorecard details</button>}
        {notice && <span role="status" data-testid="proposal-generator-notice" className="text-sm text-emerald-700">{notice}</span>}
      </div>

      {/* History */}
      {history.length > 1 && (
        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          {history.map((h) => (
            <button key={h.id} data-testid={`proposal-history-${h.id}`} onClick={() => setProposal(h)}
              className={`rounded-full border px-3 py-1 ${proposal?.id === h.id ? "border-teal-600 bg-teal-50" : "border-slate-200"}`}>
              {h.mode} · {new Date(h.created_at).toLocaleString()}
            </button>
          ))}
        </div>
      )}

      {/* Output */}
      {proposal && (
        <div className="mt-6 rounded-lg border border-slate-200 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-4 py-2">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {proposal.readiness && (
                <span data-testid="proposal-readiness" className={`rounded-full px-2.5 py-1 font-medium ${readinessStyle[proposal.readiness] || "bg-slate-100"}`}>
                  {proposal.readiness === 'Ready to send' ? 'Draft — review required' : proposal.readiness}
                </span>
              )}
              {proposal.cached && <span data-testid="proposal-cache-status" className="rounded-full bg-slate-200 px-2.5 py-1">Saved copy · no new AI call</span>}
            </div>
            <div className="flex flex-wrap gap-2 text-sm">
              <button data-testid="proposal-copy" onClick={copy} className="rounded border border-slate-300 px-3 py-1 hover:bg-white inline-flex items-center gap-1"><Copy size={14} />Copy</button>
              <button data-testid="proposal-download" onClick={download} className="rounded border border-slate-300 px-3 py-1 hover:bg-white inline-flex items-center gap-1"><Download size={14} />Download .md</button>
              <button data-testid="proposal-print" onClick={printPdf} className="rounded border border-slate-300 px-3 py-1 hover:bg-white inline-flex items-center gap-1"><Printer size={14} />Print / PDF</button>
            </div>
          </div>
          <article ref={reportBody} id="samanvai-proposal-body" data-testid="proposal-output" className="proposal-markdown px-4 sm:px-6 py-5">
            <ReactMarkdown skipHtml remarkPlugins={[remarkGfm]} components={{
              img: () => null,
              table: ({ children }) => <div className="overflow-x-auto"><table>{children}</table></div>,
              a: ({ children, href, node }) => <a data-testid={`proposal-link-${node.position?.start.offset}`} href={href} target="_blank" rel="noopener noreferrer">{children}</a>,
            }}>{clientMarkdown}</ReactMarkdown>
          </article>
          {readinessNotes && <details data-testid="proposal-readiness-notes" className="border-t border-gray-200 px-4 sm:px-6 py-4 print:hidden">
            <summary data-testid="proposal-readiness-toggle" className="cursor-pointer text-sm font-semibold text-amber-800">Internal readiness notes</summary>
            <div className="proposal-markdown pt-2" data-testid="proposal-readiness-content"><ReactMarkdown skipHtml remarkPlugins={[remarkGfm]} components={{
              img: () => null,
              table: ({ children }) => <div className="overflow-x-auto"><table>{children}</table></div>,
              a: ({ children, href, node }) => <a data-testid={`proposal-readiness-link-${node.position?.start.offset}`} href={href} target="_blank" rel="noopener noreferrer">{children}</a>,
            }}>{readinessNotes}</ReactMarkdown></div>
          </details>}
        </div>
      )}
    </section>
  );
}
